<template>
  <div class="visualizer-container">
    <div class="visua-center" ref="center">
      <template
        v-if="
          status === statusMap.connecting || status === statusMap.disconnect
        "
      >
        <div class="visua-guide">
          <div class="visua-gif"></div>
          <div class="guide-banner">{{ connectMessage || "打开应用，轻扇三下" }}</div>
          <div class="guide-tip" style="display: none">
            可视化事件定义需要诸葛io IOS SDK 2.0 以上版本。
            <a
              href="https://sdk.zhugeio.com/Zhuge_iOS_SDK.zip"
            >(下载)</a>
          </div>
        </div>
      </template>
      <template v-if="status === statusMap.connected">
        <ul class="visua-log" ref="toLogs">
          <transition name="fade" v-for="(item) in debugLogs" :key="item.id">
            <li>
              <span class="zg_icons icon_hand"></span>
              {{item.name}}
            </li>
          </transition>
        </ul>
        <div class="visua-scene" :style="sceneStyle" ref="scene">
          <img :src="bgImgSrc" :width="sceneRect.width" :height="sceneRect.height" />
          <canvas
            ref="canvas"
            :width="sceneRect.width"
            :height="sceneRect.height"
            :style="{ left: sceneRect.width + 'px', border: '1px solid red' }"
          ></canvas>
          <div class="tree-container">
            <div
              :class="{
                'clickable-ele': !checkDisableElement(item.element),
                'visua-checked': existEventIds.includes(item.element.orderIndex)
              }"
              v-for="item in clickableElements"
              :key="item.element.id"
              :style="item.style"
              @click="onClickElement(item.element)"
            ></div>
          </div>
        </div>
      </template>
    </div>
    <event-list @bindEvents="sendMsg"></event-list>
    <c-dialog
      title="添加事件"
      v-if="showAddDialog"
      class="visua-add-dialog"
      enableLoading
      @confirm="onAddEvent"
      @cancel="showAddDialog = false"
    >
      <label>事件名称:</label>
      <c-input
        :width="270"
        v-model="eventName"
        autofocus
        :autoValid="false"
        :message="nameTip"
        :validator="nameValidator"
        ref="eventName"
      ></c-input>
    </c-dialog>
  </div>
</template>
<script>
import { mapState } from "vuex";
import EventList from "./eventList";
import dataHandle from "./dataHandle";
import { util } from "../../utils";
import { actions } from "../../store/constants";
import classDefinitions from './iosClassDefinitions'
import iosClassDefinitions from './iosClassDefinitions';

let socket = null
export default {
  name: "iosVisualizer",
  components: {
    EventList
  },
  props: {
    socketUrl: {
      type: String,
      default: 'wss://codeless.zhugeio.com/connect'
    }
  },
  data() {
    return {
      logInterval: null,
      statusMap: {
        connected: 1, // 已连接
        connecting: 2, // 连接中
        disconnect: 3 // 未连接
      },
      status: 3,
      connectMessage: "",
      preHash: "", // 截图的hash，避免重复渲染用的
      deviceInfo: null,
      snapshotInterval: null, // 截屏请求轮询
      isFirstSnapRequest: true, // 是否是首次截屏请求
      bgImgSrc: "",
      scale: 1, // 缩放比例
      sceneRect: {
        width: 100,
        height: 100
      },
      debugLogs: [],
      // 可点击元素
      clickableElements: [],
      showAddDialog: false,
      eventName: "",
      nameTip: "", // 事件名称校验后的提示语
      chosenElement: null // 点击选中的元素
    };
  },
  computed: {
    ...mapState({
      currentApp(state) {
        return state.bizSys.currentApp;
      },
      eventList(state) {
        return state.bizVisualizer.eventList;
      }
    }),
    existEventIds() {
      return this.eventList.map(event =>
        event.eventJson ? event.eventJson.id : event.id
      );
    },
    sceneStyle() {
      return {
        width: `${this.sceneRect.width}px`,
        height: `${this.sceneRect.height}px`
      };
    }
  },
  mounted() {
    this.initSocket();
  },
  beforeDestroy() {
    this.onClose()
  },
  methods: {
    initSocket() {
      socket = new WebSocket(
        `${this.socketUrl}?ctype=server&appkey=${this.currentApp.appKey}&platform=ios`
      );
      socket.onmessage = this.onMessage;
      socket.onclose = this.onClose;
      socket.onerror = this.onClose;
    },
    onMessage(event) {
      const msg = event.data;
      if (!msg) return;
      const res = JSON.parse(msg);
      if (res.code) {
        // 未连接
        console.log("尚未发现设备");
        this.status = this.statusMap.disconnect;
        this.connectMessage = res.reason
          ? res.reason.replace("client未连接", "尚未发现设备")
          : "client未连接";
      } else if (res.type) {
        switch (res.type) {
          case "quit": // 连接已断开
            console.log("手机端连接已断开");
            this.status = this.statusMap.disconnect;
            this.connectMessage = "手机端连接已断开";
            this.preHash = ""
            this.deviceInfo = null
            this.isFirstSnapRequest = true
            clearInterval(this.snapshotInterval)
            break;
          case "device_info_response": // 设备详情信息
            console.log("收到设备详情", res);
            this.deviceInfo = res.payload;
            this.requestSnapshot();
            this.snapshotInterval = setInterval(() => {
              this.requestSnapshot();
            }, 4000);
            break;
          case "enter": // 客户端连接成功
            if (res.role !== "client") break;
            console.log("客户端连接成功");
            this.status = this.statusMap.connecting;
            this.connectMessage = "设备检测成功，数据连接中...";
            // 发送设备详情请求
            socket.send('{"type": "device_info_request"}');
            break;
          case "snapshot_response": // 截屏信息
            console.log("收到截屏信息", res.payload.image_hash, res);
            this.status = this.statusMap.connected;
            this.$nextTick(() => {
              if (this.preHash !== res.payload.image_hash && res.payload.serialized_objects) {
                this.preHash = res.payload.image_hash;
                this.messageHandle(res);
              }
            });
            break;
          case "track_message": // 实时调试内容处理
            console.log("收到实时调试数据", res);
            this.debug(res);
            break;
        }
      }
    },
    /**
     * 请求页面数据
     */
    requestSnapshot() {
      const param = {
        payload: {},
        type: "snapshot_request"
      }
      if (this.isFirstSnapRequest) {
        param.payload.config = iosClassDefinitions
      } else {
        param.payload.image_hash = this.preHash
      }
      socket.send(
        JSON.stringify(param)
      );
      this.isFirstSnapRequest = false
    },
    onClose() {
      clearInterval(this.snapshotInterval)
      this.status = this.statusMap.disconnect
      this.connectMessage = "连接中断，请刷新页面尝试重新连接"
      socket.onmessage = null
      socket.onclose = null
      socket.onerror = null
      socket = null
      this.preHash = ""
      this.deviceInfo = null
      this.$nextTick(() => {
        this.initSocket()
      })
    },
    messageHandle(res) {
      dataHandle.data = dataHandle.getData(res);
      dataHandle.setDataOrderIndex();
      let rootObject_id = res.payload.serialized_objects.rootObject;
      let contentTree = dataHandle.getItemById(rootObject_id);
      console.log(contentTree)
      dataHandle.getControllerTree(contentTree);
      dataHandle.getDataTree(contentTree);
      this.setBg(res.payload.screenshot, dataHandle.getFrame(contentTree));
      this.drawElements(contentTree);
    },
    /**
     * 显示背景图
     */
    setBg(screenshot, rect) {
      this.bgImgSrc = "data:image/png;base64," + screenshot;
      const containerHeight = this.$refs.center.getBoundingClientRect().height;
      const scale = (this.scale = containerHeight / rect.Height);
      this.sceneRect = {
        height: rect.Height * scale,
        width: rect.Width * scale
      };
    },
    /**
     * 渲染元素树，即把页面中所有元素都渲染出来
     */
    drawElements(elementTree) {
      this.clickableElements = [];
      this.drawElement(elementTree);
    },
    getElementRect(element) {
      const position = dataHandle.getPosition(element);
      const rect = {
        width: dataHandle.getFrame(element).Width * this.scale,
        height: dataHandle.getFrame(element).Height * this.scale,
        top: position.top * this.scale,
        left: position.left * this.scale
      };
      return rect;
    },
    drawElement(element) {
      if (!element.properties) return;
      if (dataHandle.isClickable(element)) {
        const rect = this.getElementRect(element);
        this.clickableElements.push({
          element,
          style: {
            top: rect.top + "px",
            left: rect.left + "px",
            width: rect.width + "px",
            height: rect.height + "px"
          }
        });
      }
      if (element.child) {
        for (let key in element.child) {
          const child = element.child[key];
          this.drawElement(child);
        }
      }
    },
    checkDisableElement(element) {
      return element.class[0] === 'UITextField'
    },
    onClickElement(element) {
      if (this.existEventIds.includes(element.orderIndex) || this.checkDisableElement(element)) {
        return;
      }
      this.chosenElement = element;
      this.showAddDialog = true;
    },
    async onAddEvent() {
      if (!this.$refs.eventName.validate()) return;
      const nameInput = this.$refs.eventName;
      try {
        const nameValid = await this.$store.dispatch(
          actions.visualizer.checkEventName,
          {
            appid: this.appId,
            eName: this.eventName
          }
        );
        if (!nameValid) {
          this.nameTip = "事件名称已存在";
          nameInput.invalid = true;
          nameInput.$refs.tip.show();
        } else {
          nameInput.invalid = false;
          nameInput.$refs.tip.hide();
        }
      } catch (e) {
        nameInput.invalid = true;
        this.nameTip = "事件名称校验失败";
        nameInput.$refs.tip.show();
      }
      this.$store.dispatch(
        actions.visualizer.addEvent,
        await this.getEventData()
      );
      this.eventName = "";
      this.showAddDialog = false;
    },
    nameValidator(name) {
      if (!name || !/\S+/.test(name)) {
        this.nameTip = "事件名称不能为空";
        return false;
      }
      let nameExist = false;
      this.eventList.forEach(event => {
        if (event.event_name === name) {
          nameExist = true;
        }
      });
      if (nameExist) {
        this.nameTip = "事件名称已存在";
        return false;
      }
      return true;
    },
    async getEventData() {
      const overview = await this.getOverview();
      let eventType = dataHandle.getEventType(this.chosenElement)
      const event = {
        _id: this.chosenElement.id,
        id: this.chosenElement.orderIndex,
        status: "add",
        path: this.chosenElement.class[0],
        control_event: null,
        event_name: this.eventName,
        event_type: eventType ? util.toUnderLine(eventType.replace(/^UI/, 'ui')) : null,
        table_delegate: null,
        collection_delegate: null,
        control_target: null,
        device_type: "iOS",
        screenshot: overview,
        screenshot_url: ""
      };
      const deviceInfo = this.deviceInfo;
      for (const prop in deviceInfo) {
        if (prop === 'available_font_families') continue
        if (prop === "app_version") {
          event[prop] = deviceInfo["app_release"];
        } else {
          event[prop] = deviceInfo[prop];
        }
      }
      event.control_event = this.getControlEvent();
      dataHandle.getPath(event.path, this.chosenElement, event, false);
      event.path =
        "/" +
        event.path
          .split("/")
          .reverse()
          .join("/");
      event.path += this.getUniquePath(event.path);
      if (/UINavigationController/.test(event.path)) {
        event.path = '/UINavigationController' + event.path.split('UINavigationController')[1]
      }
      return event;
    },
    getUniquePath(path = '') {
      let map = ["mp_varA", "mp_varB", "mp_varC", "mp_varD", "mp_varE"];
      let properties = this.chosenElement.properties || {};
      if (path.includes('UITabBarButton')) {
        const match = this.chosenElement.orderIndex.match(/UITabBarButton_(\d+)/)
        return `[${match[1]}]`
      }
      if (properties.tag.values[0].value !== 0) {
        return "[tag == " + properties.tag.values[0].value + "]";
      }
      for (var i = 0; i < map.length; i++) {
        if (properties[map[i]] && properties[map[i]].values[0].value) {
          return (
            "[(mp_fingerprintVersion >= 1 AND " +
            map[i] +
            ' == "' +
            properties[map[i]].values[0].value +
            '" AND tag == ' +
            properties.tag.values[0].value +
            ")]"
          );
        }
      }
      return "";
    },
    getControlEvent() {
      const action = dataHandle.actions;
      for (const key in action) {
        if (action.hasOwnProperty(key)) {
          for (let i = 0; i < this.chosenElement.class.length; i++) {
            if (action[key].includes(this.chosenElement.class[i])) {
              return parseInt(key);
            }
          }
        }
      }
      return null;
    },
    async getOverview() {
      const canvas = this.$refs.canvas;
      const context = canvas.getContext("2d");
      await this.resetCanvas(context);
      const elementRect = this.getElementRect(this.chosenElement);
      context.fillStyle = "rgba(248, 107, 79, 0.7)";
      context.strokeStyle = "#fff";
      context.lineWidth = 2;
      context.beginPath();
      context.rect(
        elementRect.left,
        elementRect.top,
        elementRect.width,
        elementRect.height
      );
      context.fill();
      context.stroke();
      context.closePath();
      return canvas.toDataURL();
    },
    resetCanvas(context) {
      return new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
          context.fillStyle = "blue";
          context.beginPath();
          context.rect(0, 0, this.sceneRect.width, this.sceneRect.height);
          context.closePath();
          context.drawImage(
            img,
            0,
            0,
            this.sceneRect.width,
            this.sceneRect.height
          );
          resolve();
        };
        img.src = this.bgImgSrc;
      });
    },
    debug(res) {
      this.debugLogs.push({
        name: res.payload.event_name,
        id: util.guid()
      })
      if (!this.logInterval) {
        this.logInterval = setInterval(() => {
          if (this.debugLogs.length) { 
            this.debugLogs.shift()
          } else {
            clearInterval(this.logInterval)
            this.logInterval = null
          }
        }, 2000)
      }
    },
    sendMsg(msg) {
      socket.send(msg);
    }
  }
};
</script>
<style lang="sass">
@import './styles/index'
</style>
